import type { AssetClass } from '@vizit/api';
import { ClearableSelect, type ClearableSelectItem } from '@vizit/organisms';

interface CollectionAssetToolbarClassificationFilterProps {
  /**
   * The asset classes available to filter by — the classes actually present in the collection,
   * in canonical display order. When empty (and not loading), the asset-class filter is not shown.
   */
  classificationOptions: AssetClass[];
  /**
   * Whether the classification options are still loading. Keeps the filter mounted (showing its
   * placeholder) during the initial load so it doesn't pop in once the options resolve.
   * @default false
   */
  isClassificationsLoading?: boolean;
  /**
   * The currently selected asset class, or `null` when "All asset classes" is selected
   */
  selectedClassification: AssetClass | null;
  /**
   * Called when the user changes the asset-class filter; `null` clears it (all classes)
   */
  onClassificationChange: (classification: AssetClass | null) => void;
}

/**
 * Asset-class filter for `CollectionAssetToolbar.Filters`. Renders nothing for a collection that
 * genuinely has no classes.
 *
 * @param props - The component props
 */
function CollectionAssetToolbarClassificationFilter(props: CollectionAssetToolbarClassificationFilterProps) {
  const {
    classificationOptions,
    isClassificationsLoading = false,
    selectedClassification,
    onClassificationChange,
  } = props;

  const classificationItems: ClearableSelectItem[] = classificationOptions.map((classification) => ({
    id: classification,
    label: classification,
  }));

  // Show the filter once the collection is known to have classes, and keep it up during the initial
  // load so it doesn't blink in. It only stays hidden for a collection that genuinely has none.
  const showClassificationFilter = isClassificationsLoading || classificationItems.length > 0;

  if (!showClassificationFilter) {
    return null;
  }

  return (
    <ClearableSelect
      ariaLabel="Filter by asset class"
      placeholder="All asset classes"
      items={classificationItems}
      selectedKey={selectedClassification}
      onSelectionChange={(key) => onClassificationChange(key as AssetClass | null)}
      isClearable={false}
      className="min-w-[256px]"
      data-testid="collection-asset-class-filter"
    />
  );
}

export { CollectionAssetToolbarClassificationFilter };
export type { CollectionAssetToolbarClassificationFilterProps };
